import React, { useState, useEffect, ReactNode, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { motion, HTMLMotionProps } from 'framer-motion';
import { loadPostContent } from '../utils/blogLoader';
import { useNavigationStore } from '../stores/navigationStore';

interface PostContentProps {
    contentPath: string;
    onVisualizationMarker?: (name: string) => React.ReactNode;
}

const getVisualizationName = (children: ReactNode): string | null => {
    if (typeof children === 'string' && children.startsWith('[') && children.endsWith(']')) {
        return children.slice(1, -1);
    }
    
    if (Array.isArray(children) && children.length > 0) {
        const firstChild = children[0];
        if (typeof firstChild === 'string' && firstChild.startsWith('[') && firstChild.endsWith(']')) {
            return firstChild.slice(1, -1);
        }
    }
    
    return null;
};

const TextReveal: React.FC<{children: React.ReactNode; delay?: number}> = ({ children, delay = 0 }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: delay, ease: "easeOut" }}
        >
            {children}
        </motion.div>
    );
};

const PostContent: React.FC<PostContentProps> = ({ contentPath, onVisualizationMarker }) => {
    const [content, setContent] = useState<string | null>(null);
    const [paragraphCount, setParagraphCount] = useState(0);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const paragraphRefs = useRef<(HTMLElement | null)[]>([]);
    const { isMobile } = useNavigationStore();
    
    useEffect(() => {
        const loadContent = async () => {
            try {
                console.log("PostContent loading content for:", contentPath);
                const postContent = await loadPostContent(contentPath);
                console.log("PostContent received content:", postContent ? "yes" : "no");
                
                if (postContent) {
                    setContent(postContent);
                    // Estimate paragraph count from newlines
                    const paragraphs = postContent.split('\n\n').filter(Boolean);
                    setParagraphCount(paragraphs.length);
                    paragraphRefs.current = new Array(paragraphs.length).fill(null);
                }
            } catch (error) {
                console.error('Error loading post content:', error);
            }
        };
        loadContent();
    }, [contentPath]);

    useEffect(() => {
        if (paragraphCount > 0) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            // Add class to make visible when intersecting
                            entry.target.classList.add('is-visible');
                            // Stop observing once visible
                            observerRef.current?.unobserve(entry.target);
                        }
                    });
                },
                { threshold: 0.1 }
            );
            
            // Observe paragraph elements
            paragraphRefs.current.forEach(el => {
                if (el) observerRef.current?.observe(el);
            });
        }
        
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [paragraphCount, content]);

    const setRefForParagraph = (index: number) => (el: HTMLElement | null) => {
        if (paragraphRefs.current && index < paragraphRefs.current.length) {
            paragraphRefs.current[index] = el;
            if (el && observerRef.current) {
                observerRef.current.observe(el);
            }
        }
    };

    if (!content) {
        return (
            <div className="flex justify-center items-center h-40">
                <div className="animate-pulse flex space-x-4">
                    <div className="flex-1 space-y-4 py-1">
                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                        <div className="space-y-2">
                            <div className="h-4 bg-gray-700 rounded"></div>
                            <div className="h-4 bg-gray-700 rounded w-5/6"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    let paragraphIndex = 0;

    const animationProps = {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.5 }
    };

    // Reduce animation delays on mobile for better performance
    const getDelay = (baseDelay: number) => {
        return isMobile ? Math.min(baseDelay * 0.5, 0.2) : baseDelay;
    };

    return (
        <div className="prose prose-invert max-w-none prose-sm md:prose-base">
            <ReactMarkdown components={{
                h1: ({ node, children, ...props }) => (
                    <motion.h1 
                        className="text-2xl md:text-3xl font-bold mb-3 md:mb-4" 
                        {...animationProps}
                        {...props as any}
                    >
                        {children}
                    </motion.h1>
                ),
                h2: ({ node, children, ...props }) => (
                    <motion.h2 
                        className="text-xl md:text-2xl font-bold mb-2 md:mb-3" 
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: getDelay(0.1) }}
                        {...props as any}
                    >
                        {children}
                    </motion.h2>
                ),
                h3: ({ node, children, ...props }) => (
                    <motion.h3 
                        className="text-lg md:text-xl font-bold mb-2" 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: getDelay(0.15) }}
                        {...props as any}
                    >
                        {children}
                    </motion.h3>
                ),
                p: ({ node, children, ...props }) => {
                    // Check if this paragraph is a visualization marker
                    const vizName = getVisualizationName(children);
                    if (vizName && onVisualizationMarker) {
                        console.log("Found visualization marker in content:", vizName);
                        const visualizationElement = onVisualizationMarker(vizName);
                        return visualizationElement ? 
                            <React.Fragment>{visualizationElement}</React.Fragment> : 
                            <p {...props}>{children}</p>;
                    }
                    
                    const currentIndex = paragraphIndex++;
                    const delay = getDelay(Math.min(0.1 + currentIndex * 0.05, 0.5));
                    
                    return (
                        <motion.p 
                            className="mb-3 md:mb-4 opacity-0 text-sm md:text-base"
                            ref={setRefForParagraph(currentIndex)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay }}
                            {...props as any}
                        >
                            {children}
                        </motion.p>
                    );
                },
                img: ({ node, src, alt, ...props }) => {
                    const imgSrc = src || '';
                    // Handle relative paths to add the correct base URL for blog images
                    const fixedSrc = imgSrc.startsWith('/') || imgSrc.startsWith('http') 
                        ? imgSrc 
                        : `/assets/media/blog/${imgSrc}`;
                        
                    return (
                        <motion.div
                            className="my-4 md:my-6"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <img 
                                src={fixedSrc}
                                alt={alt}
                                className="rounded-lg shadow-md max-w-full mx-auto"
                                loading="lazy"
                                style={{ 
                                    maxHeight: isMobile ? '400px' : '600px', 
                                    objectFit: 'contain',
                                    width: '100%'
                                }}
                                onError={(e) => {
                                    console.error(`Failed to load image: ${fixedSrc}`);
                                    const target = e.target as HTMLImageElement;
                                    target.onerror = null;
                                    target.src = '/assets/media/placeholder.png';
                                }}
                                {...props as any}
                            />
                            {alt && (
                                <p className="text-xs md:text-sm text-center text-gray-400 mt-1 md:mt-2">{alt}</p>
                            )}
                        </motion.div>
                    );
                },
                ul: ({ node, children, ...props }) => (
                    <motion.ul 
                        className="mb-3 md:mb-4 list-disc pl-4 md:pl-6" 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        {...props as any}
                    >
                        {children}
                    </motion.ul>
                ),
                ol: ({ node, children, ...props }) => (
                    <motion.ol 
                        className="mb-3 md:mb-4 list-decimal pl-4 md:pl-6" 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        {...props as any}
                    >
                        {children}
                    </motion.ol>
                ),
                li: ({ node, ...props }) => <li className="mb-1 text-sm md:text-base" {...props} />,
                a: ({ node, ...props }) => <a className="text-blue-400 hover:underline" {...props} />,
                blockquote: ({ node, children, ...props }) => (
                    <motion.blockquote 
                        className="pl-3 md:pl-4 border-l-4 border-gray-700 italic my-3 md:my-4 text-sm md:text-base" 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        {...props as any}
                    >
                        {children}
                    </motion.blockquote>
                ),
                code: ({ node, ...props }) => {
                    // Check if the code is inline (not wrapped in a pre tag)
                    const isInline = !(props.className && props.className.includes('language-'));
                    
                    if (isInline) {
                        return (
                            <code 
                                className="bg-gray-800 px-1 py-0.5 rounded text-xs md:text-sm font-mono" 
                                {...props} 
                            />
                        );
                    }
                    
                    return (
                        <motion.div
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="my-3 md:my-4"
                        >
                            <code className="text-xs md:text-sm font-mono block bg-gray-900 p-2 md:p-4 rounded-lg overflow-x-auto" {...props} />
                        </motion.div>
                    );
                },
                pre: ({ node, children, ...props }) => (
                    <pre className="bg-transparent p-0 overflow-x-auto" {...props}>
                        {children}
                    </pre>
                ),
                table: ({ node, children, ...props }) => (
                    <div className="overflow-x-auto my-3 md:my-4">
                        <table className="min-w-full divide-y divide-gray-700" {...props}>
                            {children}
                        </table>
                    </div>
                ),
                th: ({ node, children, ...props }) => (
                    <th className="px-2 md:px-3 py-2 text-left text-xs md:text-sm font-medium text-gray-300 uppercase tracking-wider" {...props}>
                        {children}
                    </th>
                ),
                td: ({ node, children, ...props }) => (
                    <td className="px-2 md:px-3 py-2 text-xs md:text-sm" {...props}>
                        {children}
                    </td>
                ),
                hr: ({ node, ...props }) => (
                    <hr className="my-4 md:my-6 border-gray-700" {...props} />
                ),
            }}>
                {content}
            </ReactMarkdown>
        </div>
    );
};

export default PostContent;
